import type { Task } from "fhir/r5";

/**
 * Utilitários de notificação baseados em FHIR Task.
 *
 * Objetivo:
 * - Centralizar regras pequenas e reutilizáveis (evitar duplicação em Header/Dashboard).
 * - Manter as telas limpas, com tipagem mínima (sem "any") para os campos usados.
 */

const TASK_CODE_SYSTEM = "https://fluxmed.com.br/fhir/CodeSystem/task-types";
const TASK_CODE_QUESTIONNAIRE_ANSWERED = "QUESTIONNAIRE_ANSWERED_NOTIFICATION";

function sanitizePatientIdForSummary(patientId: string, isDefaultApp: boolean) {
  return isDefaultApp ? patientId.replace(/[^a-zA-Z0-9]/g, "") : patientId;
}

function extractPatientIdFromPatientReference(patientRef: string) {
  const patientId = patientRef.split("-")?.[1];
  return patientId || null;
}

/**
 * Identifica a notificação de "resposta de questionário" criada pelo fluxo de
 * `QuestionnaireRespondPage` (taskTypes: QUESTIONNAIRE_ANSWERED_NOTIFICATION).
 */
export function isQuestionnaireAnsweredNotificationTask(task: Task | unknown) {
  if (!task || typeof task !== "object") return false;
  const t = task as Task;

  const codings = t.code?.coding ?? [];
  const hasAnsweredCoding = Array.isArray(codings)
    ? codings.some(
        (coding) =>
          coding?.system === TASK_CODE_SYSTEM &&
          coding?.code === TASK_CODE_QUESTIONNAIRE_ANSWERED,
      )
    : false;

  // Fallback: texto (mantém compatibilidade em caso de payloads sem coding)
  const codeText = typeof t.code?.text === "string" ? t.code.text.trim() : "";
  const hasAnsweredText = codeText.toLowerCase() === "questionário respondido";

  return hasAnsweredCoding || hasAnsweredText;
}

/**
 * Deriva o `patientId` necessário para navegar para `/summary`.
 *
 * Fonte principal:
 * - `task.requester.reference` (no fluxo atual de questionário respondido, o requester é o Patient)
 *
 * Fallback:
 * - `task.for.reference` (caso alguma integração use `for` como Patient)
 */
export function getPatientIdForSummaryFromTask(
  task: Task | unknown,
  options: { isDefaultApp: boolean },
) {
  if (!task || typeof task !== "object") return null;
  const t = task as Task;

  const requesterRef = t.requester?.reference;
  if (typeof requesterRef === "string") {
    const extracted = extractPatientIdFromPatientReference(requesterRef);
    if (extracted)
      return sanitizePatientIdForSummary(extracted, options.isDefaultApp);
  }

  const forRef = (t as any)?.for?.reference as unknown;
  if (typeof forRef === "string") {
    const extracted = extractPatientIdFromPatientReference(forRef);
    if (extracted)
      return sanitizePatientIdForSummary(extracted, options.isDefaultApp);
  }

  return null;
}
